/**
 * Theme Utilities
 * Helper functions and utilities for working with the design system
 */

import { tokens } from './tokens.js';

/**
 * Generate CSS class names for consistent styling
 */
export const classNames = {
  // Button variants
  button: {
    primary: 'bg-primary-500 hover:bg-primary-600 text-white font-medium transition-colors',
    secondary: 'bg-neutral-800 hover:bg-neutral-700 text-neutral-50 font-medium transition-colors border border-neutral-600',
    outline: 'border-2 border-primary-500 text-primary-500 hover:bg-primary-500 hover:text-white font-medium transition-all',
    ghost: 'text-neutral-400 hover:text-neutral-50 hover:bg-neutral-800 font-medium transition-colors',
    danger: 'bg-error text-white hover:bg-red-600 font-medium transition-colors'
  },

  // Card variants
  card: {
    base: 'bg-neutral-900 border border-neutral-800 rounded-xl',
    elevated: 'bg-neutral-900 border border-neutral-800 rounded-xl shadow-lg',
    interactive: 'bg-neutral-900 border border-neutral-800 rounded-xl hover:border-neutral-700 transition-colors cursor-pointer'
  },

  // Text variants
  text: {
    heading: 'text-neutral-50 font-bold',
    subheading: 'text-neutral-50 font-semibold',
    body: 'text-neutral-400',
    muted: 'text-neutral-500',
    caption: 'text-neutral-500 text-sm'
  },

  // Input variants
  input: {
    base: 'bg-neutral-950 border border-neutral-800 rounded-lg text-neutral-50 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all',
    error: 'bg-neutral-950 border border-red-500 rounded-lg text-neutral-50 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all'
  },

  // Status indicators
  status: {
    online: 'bg-success text-white',
    offline: 'bg-neutral-600 text-white',
    away: 'bg-warning text-white',
    busy: 'bg-error text-white'
  },

  // Position badges (for fantasy football)
  position: {
    goalkeeper: 'bg-info text-white',
    defender: 'bg-success text-white',
    midfielder: 'bg-warning text-white',
    forward: 'bg-error text-white'
  }
};

/**
 * Get responsive class names
 */
export const responsive = {
  // Grid layouts
  grid: {
    auto: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
    dashboard: 'grid grid-cols-1 lg:grid-cols-12',
    sidebar: 'grid grid-cols-1 lg:grid-cols-4',
    mobile: 'grid grid-cols-1 gap-4',
    tablet: 'grid grid-cols-2 gap-6',
    desktop: 'grid grid-cols-3 gap-8'
  },

  // Flexbox layouts
  flex: {
    center: 'flex items-center justify-center',
    between: 'flex items-center justify-between',
    start: 'flex items-center justify-start',
    column: 'flex flex-col',
    wrap: 'flex flex-wrap',
    mobileColumn: 'flex flex-col sm:flex-row',
    mobileCenter: 'flex flex-col items-center sm:flex-row sm:items-start'
  },

  // Spacing
  spacing: {
    section: 'py-8 sm:py-12 lg:py-24',
    container: 'px-4 sm:px-6 lg:px-8',
    card: 'p-4 sm:p-6 lg:p-8',
    mobile: 'p-4',
    tablet: 'p-6',
    desktop: 'p-8'
  },

  // Typography
  text: {
    heading: 'text-2xl sm:text-3xl md:text-4xl lg:text-5xl',
    subheading: 'text-lg sm:text-xl md:text-2xl lg:text-3xl',
    body: 'text-sm sm:text-base md:text-lg',
    caption: 'text-xs sm:text-sm',
    button: 'text-sm sm:text-base'
  },

  // Mobile-specific utilities
  mobile: {
    hidden: 'hidden sm:block',
    only: 'block sm:hidden',
    stack: 'flex flex-col space-y-4',
    row: 'flex flex-col sm:flex-row sm:space-x-4 sm:space-y-0 space-y-4',
    fullWidth: 'w-full sm:w-auto',
    centerMobile: 'text-center sm:text-left'
  },

  // Touch-friendly sizing
  touch: {
    button: 'min-h-[44px] min-w-[44px]', // iOS recommended touch target
    input: 'min-h-[44px]',
    tap: 'p-3', // Generous padding for touch
    spacing: 'space-y-4 sm:space-y-6' // Larger spacing on mobile
  }
};

/**
 * Animation utilities
 */
export const animations = {
  // Entrance animations
  entrance: {
    fadeIn: 'animate-fade-in',
    slideUp: 'animate-slide-up',
    scaleIn: 'animate-scale-in',
    slideInRight: 'animate-slide-in-right'
  },

  // Loading animations
  loading: {
    spin: 'animate-spin',
    pulse: 'animate-pulse',
    bounce: 'animate-bounce',
    pulseGreen: 'animate-pulse-green'
  },

  // Hover effects
  hover: {
    scale: 'hover:scale-105 transition-transform',
    lift: 'hover:-translate-y-1 hover:shadow-lg transition-all',
    glow: 'hover:shadow-primary-500/20 hover:shadow-lg transition-shadow'
  }
};

/**
 * Component size utilities
 */
export const sizes = {
  button: {
    sm: 'h-button-sm px-3 text-sm',
    md: 'h-button-md px-4 text-base',
    lg: 'h-button-lg px-6 text-lg'
  },

  input: {
    sm: 'h-input-sm px-3 text-sm',
    md: 'h-input-md px-4 text-base',
    lg: 'h-input-lg px-6 text-lg'
  },

  avatar: {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16',
    xl: 'w-20 h-20'
  }
};

/**
 * Z-index utilities
 */
export const zIndex = {
  dropdown: 'z-dropdown',
  sticky: 'z-sticky',
  fixed: 'z-fixed',
  modal: 'z-modal',
  tooltip: 'z-tooltip'
};

/**
 * Utility function to combine class names
 */
export const cn = (...classes) => {
  return classes.filter(Boolean).join(' ');
};

/**
 * Get color with opacity
 */
export const withOpacity = (color, opacity) => {
  if (color.startsWith('#')) {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  return color;
};

/**
 * Generate component variants
 */
export const createVariants = (base, variants) => {
  return Object.entries(variants).reduce((acc, [key, variant]) => {
    acc[key] = cn(base, variant);
    return acc;
  }, {});
};

/**
 * Breakpoint utilities
 */
export const breakpoints = {
  sm: '(min-width: 640px)',
  md: '(min-width: 768px)',
  lg: '(min-width: 1024px)',
  xl: '(min-width: 1280px)',
  '2xl': '(min-width: 1536px)'
};

/**
 * Media query helper
 */
export const mediaQuery = (breakpoint) => {
  return `@media ${breakpoints[breakpoint]}`;
};

/**
 * Theme utilities object
 */
const themeUtils = {
  classNames,
  responsive,
  animations,
  sizes,
  zIndex,
  cn,
  withOpacity,
  createVariants,
  breakpoints,
  mediaQuery
};

export default themeUtils;